import { DashifyEditorHeaderControls } from './headerControls/DashifyEditorHeaderControls';
import { useRouter } from 'next/router';
import { MAGNETIC_TOKENS, css, Overlay, Icon, Layout, utils, Drawer } from '@appd/uik';
import { DashboardViewer } from '../components';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardContext, UNDO_REDO_EVENT_TYPE, useDashboardJson, useUndoRedo } from '../../hooks';
import {
  cloneDeep,
  find,
  first,
  get,
  includes,
  isString,
  keys,
  last,
  reduce,
  reject,
  set,
  split,
  unset,
  values,
} from 'lodash';
import { DashboardJson, Datasource, ExpressionJson } from '../models';
import { getGlobals, emitter, setGlobals, useTemplateProcessorList } from '../components/DashboardViewer/globals';
import { ComponentType, ComponentTypeOption, WidgetProps } from '../components/widget/models';
import { cloneDashboard, saveTemplateChangesToOrion } from '../../utils';
import { DashifyDescription } from './description/DashifyDescription';
import { DashifyPropertiesPanel } from './properties/DashifyPropertiesPanel';
import { DashifyInspectPanel } from './inspect/DashifyInspectPanel';
import { DashifyHeader } from './header/DashifyHeader';
import { DataPanel } from '../components/widget/components';
import { ActionType, getActionsRegistry } from '../actions';
import { UNCONTAINED_WIDGET_JSON_POINTERS } from '../components/widget/constants';
import { getWidgetPropertiesFromTemplate, getWidgetTemplateWithUpdatedWidgetProps } from '../components/widget/utils';
import {
  generateTemplate,
  getComponentTypeFromName,
  getTemplateWithUpdatedExpressionFormat,
  randomNumber,
  view,
} from './editorUtils';

interface DashifyEditorProps {
  request?: any;
  template?: DashboardJson;
}

const {
  COLORS: { INTERACT_BG, INTERACT_TEXT_WEAK },
  TYPOGRAPHY: { P3_EMPHASIS },
} = MAGNETIC_TOKENS;

css.RENDERER.renderStatic(
  {
    // Prevent layout accidents in a dashboard from causing double scrollbars in the outer content.
    contain: 'size',

    // Ensure <Overlay> elements remain correctly positioned while scrolling.
    // <Overlay.Provider> positions itself absolutely. This style ensures the parent is the offset parent.
    position: 'relative',
  },
  'div:has(#fso-dashify)',
);

const LINK_STYLES = css({
  ':hover': {
    '& path': { fill: `${INTERACT_BG} !important` },
    color: INTERACT_BG,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
  },
  alignItems: 'center',
  alignSelf: 'flex-start',
  display: 'flex',
  color: INTERACT_TEXT_WEAK,
  font: P3_EMPHASIS,
  gap: 4,
  textDecoration: 'none',
});

export const DashifyEditor: React.FC<DashifyEditorProps> = ({ request, template: rawTemplate = {} }) => {
  // ⚠️ Do not allow people to use `template` prop as-is, because it can quickly become stale.
  //
  // The `updateTemplate()` function is called in a bunch of places, which causes the `TemplateRegistry` to diverge from
  // the `template` prop.
  //
  // This is a ref because some functions like `queryChangeListener` capture this variable once and then don't update
  // again. We must keep those captures synced always with the latest saved value (`rootRef.current`).
  const rootRef = utils.useLiveRef(useDashboardJson(rawTemplate) ?? rawTemplate);

  // ⚠️ DO NOT REMOVE rootRef.current, we need proper reference to selected widget to show in the edit mode
  useEffect(() => setName(rootRef.current.name), [rootRef.current]);

  const router = useRouter();
  const syncLayout = utils.useLiveCallback(() => view.syncLayout(editingTemplate));
  const { actionsRegistry, widgetActions } = useMemo(
    (actionsRegistry = getActionsRegistry(router)) => ({
      actionsRegistry,
      widgetActions: view.widget.actions(actionsRegistry, syncLayout),
    }),
    [router],
  );
  const globals = getGlobals();
  const tmplList = useTemplateProcessorList();

  const [isEditMode, setEditMode] = useState(false);
  const [isWidgetEditMode, setWidgetEditMode] = useState(false);
  const [isInspectMode, setIsInspectMode] = useState(false);
  const [isFullInspectMode, setFullInspectMode] = useState(false);
  const [isStale, setIsStale] = useState(true);
  /**
   * Turns out this is needed because `TemplateViewRenderer` reads its template from `useTemplateStore` and doesn't
   * listen for changes to TemplateRegistry like `useDashboardJson` does.
   *
   * This needs to be bumped whenever we call `updateTemplate`, or the `TemplateViewRenderer` shows stale results.
   *
   * This is a very ugly workaround made necessary by framework-level shortcomings.
   */
  const [viewerKey, setDashboardViewerKey] = useState(Date.now());
  const [isTemplate, setTemplate] = useState(false);

  // Header
  const [title, setTitle] = useState(rootRef.current.title);
  const [description, setDescription] = useState(rootRef.current.description);
  const [showDescription, setShowDescription] = useState<boolean>(
    get(rootRef.current, 'spec.editor.showDescriptionInViewMode', true) as boolean,
  );
  const showPageTitle = rootRef.current.showPageTitle ?? true;

  // Template Specs
  const [viewingTemplate, setViewingTemplate] = useState(cloneDeep(rootRef.current) as DashboardJson);
  const [editingTemplate, setEditingTemplate] = useState(viewingTemplate);
  const [name, setName] = useState(viewingTemplate.name);
  const [editTemplateName, setEditTemplateName] = useState(name); // used to keep track of template being edited

  const { updateTemplate, addTemplate } = useContext(DashboardContext);
  const { filter } = request?.getTopologyContext() ?? {};

  // Partial
  const [selectedComponentType, setSelectedComponentType] = useState<ComponentTypeOption>(ComponentType.WIDGET);

  // Template Specs
  const [expressionsJSON, setExpressionJSON] = useState<Record<string, ExpressionJson>>(
    get(rootRef.current, 'spec.expressions', {}) as Record<string, ExpressionJson>,
  );
  const [datasourceJSON, setDatasourceJSON] = useState<Record<string, Datasource>>(
    get(rootRef.current, 'spec.datasources', {}) as Record<string, Datasource>,
  );
  const [propsJSON, setPropsJSON] = useState<any>(get(rootRef.current, 'spec.props', {}));
  const [filterKeysByVarName, setFilterKeysByVarName] = useState<Record<string, string>>({});
  const [queryResult, setQueryResult] = useState<Record<string, unknown>>({});
  const [selectedInspectedQueryKey, setSelectedInspectedQueryKey] = useState('');

  /**
   * Hooks
   */
  const undoRedoActions = useUndoRedo();

  /**
   * Consider initial load view state.
   * Configure filter bar.
   */
  useEffect(() => {
    setFilterBar(viewingTemplate);
    queryChangeListener(router.asPath);
  }, []);

  /**
   * Listens to filter events to update the globals.filterStr.
   * globals.filterStr is referenced by all dataSources
   * to automatically apply filters w/o manual configuration.
   */
  useEffect(() => {
    const onGlobalsChanged = ({ path = '' }: any) => {
      if (path !== '/globals/filterStr') {
        setFilterStr();
      }
    };

    emitter && emitter.on('globalsChanged', onGlobalsChanged);

    return () => {
      emitter && emitter.off('globalsChanged', onGlobalsChanged);
    };
  }, [emitter]);

  /**
   * Considers if should enter widget edit view.
   * If so,
   *  pull child template from parent template
   *  set child template as viewingTemplate to render
   *  switch view to widget edit mode
   *
   * Also considers if creating new widget
   * If so,
   *  read path if /new/filter or /new/widget
   *  generate base template
   *  assign unique names
   *
   *  !! IMPORTANT !!
   *  if new filter,
   *   ensure name includes ':filter:'
   *
   * Route changes are triggered by user selection on
   * dashify:Widget & actionsRegistry module.
   *
   * @param path router.asPath
   */
  useEffect(() => {
    router.events.on('routeChangeComplete', queryChangeListener);
    router.beforePopState(({ as }) => {
      if (as !== router.asPath && globals.editMode) {
        // Runs when leaving an editor page; on back/forward browser button actions
        if (!window.confirm('Any unsaved changes will be discarded. Are you sure you want to navigate away?')) {
          return false;
        }
      }
      revertAllEditsAndSwitchToViewMode();
      return true;
    });

    return () => {
      router.events.off('routeChangeComplete', queryChangeListener);
      router.beforePopState(() => true);
    };
  }, [router.asPath]);

  /**
   * Pull the datasource's query results to provide
   * to JSONEditor component in Inspect Panel
   */
  useEffect(() => {
    const tmpl = find(values(tmplList), ['output.name', name]);
    setQueryResult({ data: get(tmpl, 'output.data') });
  }, [isInspectMode]);

  /**
   * DASHUI-8521
   * It's ok to write to rootRef when title and description are updated
   * b/c on cancel, the template is retrieved from orion and that's what gets set
   */
  useEffect(() => {
    if ((isString(title) || isString(description)) && !isTemplate) {
      rootRef.current.title = title;
      rootRef.current.description = description;
    }
  }, [title, description]);

  useEffect(() => {
    setTemplate(rootRef.current.author == 'System');
  }, [rawTemplate]);

  /**
   * See note in emitter useEffect.
   */
  const setFilterStr = () => {
    const filterStr = reduce(
      get(globals, 'variables', {} as any),
      (mem: string, value, varName) => {
        const key = filterKeysByVarName[varName];
        return Array.isArray(value) && value.length ? `${mem}[${key} IN ${JSON.stringify(value)}]` : mem;
      },
      '[isActive=true]',
    );

    if (globals.filterStr !== filterStr) {
      setGlobals('/globals/filterStr', filterStr);
    }
  };

  /**
   * See note in router.asPath useEffect
   */
  const queryChangeListener = (path: string) => {
    const paths = split(path, '/');
    const action = paths[paths.length - 2];
    const name = first(split(last(paths), '?')) as string;
    const type = getComponentTypeFromName(name);

    /**
     * Dashoard edit mode
     * eg: /cco/test:inlineDemo/edit
     */
    if (name === ActionType.EDIT) {
      enterDashboardEditMode();
    }

    /**
     * Widget edit mode
     * eg: /cco/test:inlineDemo/edit/:widget:12345
     *
     * Moves to /cco/test:inlineDemo
     * when an unknown component type is passed
     *
     * eg: /cco/test:inlineDemo/edit/:invalid:12345
     */
    if (action === ActionType.EDIT) {
      editWidget(name, type);
      setSelectedComponentType(type);
    }

    /**
     * New ComponentType
     * eg: /cco/test:inlineDemo/new/widget
     */
    if (action === ActionType.NEW && includes(values(ComponentType), name)) {
      createNewWidget(name as ComponentTypeOption);
    }

    /**
     * Delete
     * Simple deletion of a template from templates[].
     */
    if (action === ActionType.DELETE) {
      deleteWidget(name);
    }

    // duplicate action flow
    if (action === ActionType.DUPLICATE) {
      duplicateWidget(name);
    }
  };

  /**
   * @todo docs
   */
  const editWidget = (name: string, type: ComponentTypeOption) => {
    const templates = get(editingTemplate, 'templates', []) as DashboardJson[];
    let tmpl = find(templates, t => t.name == name);

    if (tmpl) {
      tmpl = cloneDeep(tmpl);
      setEditTemplateName(tmpl.name ?? '');
      set(tmpl, 'name', `${tmpl.name}${randomNumber()}`);

      if (type !== ComponentType.FILTER) {
        tmpl.templates = rootRef.current.templates;
        setFilterBar(tmpl, false);
      }

      // without setTimeout, template was getting added too slowly for DashboardViewer's useDashboard() to find
      setTimeout(() => addTemplate(tmpl as DashboardJson));
      setTemplateSpecs(tmpl);

      setEditMode(false);
      setWidgetEditMode(true);
      setGlobals('/globals/editMode', false);
      setGlobals('/globals/widgetEditMode', true);
      setDashboardViewerKey(Date.now());
    } else {
      router.push(`/${router.query.appName}/${rootRef.current.name}`);
      return;
    }

    updateTemplate(rootRef.current);
  };

  /**
   * Creates a new/copy a widget/filter.
   *
   * if widget (not a filter),
   *  inject widget into spec.view
   */
  const createNewWidget = (type: ComponentTypeOption) => {
    // output > root:filter:f1234 | root:widget:w1234
    const name = [rootRef.current.name, type, `${type[0]}${Date.now()}`].join(':');
    const editingTmpl = cloneDeep(rootRef.current);
    const newTmpl = generateTemplate(name, type);

    if (type !== ComponentType.FILTER) {
      newTmpl.templates = rootRef.current.templates;
      setFilterBar(newTmpl, false);

      view.widget.add(editingTmpl, name);
    }

    Array.isArray(editingTmpl.templates) && editingTmpl.templates.push(newTmpl);

    addTemplate(newTmpl);
    updateTemplate(editingTmpl);
    setEditingTemplate(editingTmpl);
    setDashboardViewerKey(Date.now());
    actionsRegistry.edit.onClick(newTmpl.name as string);
  };

  /**
   *
   * @param name
   */
  const deleteWidget = (name: string) => {
    editingTemplate.templates = reject(editingTemplate.templates, t => t.name === name);

    view.widget.remove(editingTemplate, name);

    setFilterBarAndUpdateTemplate(editingTemplate);
    router.replace(`/${router.query.appName}/${rootRef.current.name}/edit`);
  };

  /**
   *
   * @param name
   */
  const duplicateWidget = (name: string) => {
    const widgetjson = editingTemplate.templates?.find(json => json.name === name);

    if (widgetjson) {
      const duplicateWidgetJson = cloneDeep(widgetjson);
      const type = getComponentTypeFromName(duplicateWidgetJson.name ?? '');

      const duplicateTemplateName = [duplicateWidgetJson.name, type, `${type[0]}${Date.now()}`].join(':');
      set(duplicateWidgetJson, 'name', duplicateTemplateName);

      view.widget.duplicate(editingTemplate, name, duplicateTemplateName);

      Array.isArray(editingTemplate.templates) && editingTemplate.templates.push(duplicateWidgetJson);

      addTemplate(duplicateWidgetJson);
      updateTemplate(editingTemplate);

      setTemplateSpecs(editingTemplate);
      setDashboardViewerKey(Date.now());
      router.replace(`/${router.query.appName}/${editingTemplate.name}/edit`);
    }
  };

  /**
   * View States
   */
  const enterDashboardEditMode = () => {
    undoRedoActions.resetStack();
    setEditMode(true);
    setWidgetEditMode(false);
    setIsInspectMode(false);
    setGlobals('/globals/editMode', true);
    setGlobals('/globals/widgetEditMode', false);
  };

  const switchToViewMode = () => {
    undoRedoActions.resetStack();
    setEditMode(false);
    setWidgetEditMode(false);
    setIsInspectMode(false);
    setFullInspectMode(false);
    setGlobals('/globals/editMode', false);
    setGlobals('/globals/widgetEditMode', false);
  };

  const revertEdit = () => {
    if (isTemplate) {
      router.push(`/${router.query.appName}`).catch(() => {});
    } else {
      revertEditsAndSwitchToViewMode();
    }
  };

  const revertEditsAndSwitchToViewMode = () => {
    if (isEditMode) {
      setIsStale(false);
      setDashboardViewerKey(Date.now());
      switchToViewMode();
    } else {
      setEditingTemplate(cloneDeep(rootRef.current));
      setFilterBarAndUpdateTemplate(rootRef.current);
      enterDashboardEditMode();
    }
  };

  const revertAllEditsAndSwitchToViewMode = () => {
    // revert editor, widgetEditor, and inspect changes
    setIsStale(false);
    setDashboardViewerKey(Date.now());
    setFilterBarAndUpdateTemplate(rootRef.current);
    switchToViewMode();
  };

  /**
   * Template Updates
   */
  const setQuery = (query?: Record<string, Datasource>) => {
    undoRedoActions.addAction(cloneDeep(viewingTemplate));
    set(viewingTemplate, 'spec.datasources', query);
    updateTemplate(viewingTemplate);
    setDashboardViewerKey(Date.now());
  };

  const setProps = (props = {}) => {
    undoRedoActions.addAction(cloneDeep(viewingTemplate));
    updateTemplate(getWidgetTemplateWithUpdatedWidgetProps(viewingTemplate, props as WidgetProps));
    setDashboardViewerKey(Date.now());
  };

  const undoRedoHandler = (eventType: UNDO_REDO_EVENT_TYPE) => {
    let tmp: DashboardJson = {};

    if (eventType == UNDO_REDO_EVENT_TYPE.Undo) {
      tmp = cloneDeep(undoRedoActions.undoAction(viewingTemplate));
    } else if (eventType == UNDO_REDO_EVENT_TYPE.Redo) {
      tmp = cloneDeep(undoRedoActions.redoAction(viewingTemplate));
    }

    setFilterBarAndUpdateTemplate(tmp);
  };

  const stageExpressionUpdate = (expr: string, key?: string, description?: string, dataSources?: string[]) => {
    if (!key) {
      key = `expression${keys(expressionsJSON).length}`;
    }
    if (!description) description = '';
    if (!dataSources) dataSources = [];
    const expressions = {
      ...expressionsJSON,
      [key]: {
        jsonata: expr,
        description: description,
        dataSources: dataSources,
      },
    };

    setExpressionJSON(expressions);

    set(viewingTemplate, 'spec.expressions', expressions);
    updateTemplate(viewingTemplate);

    setIsInspectMode(false);
    setFullInspectMode(false);
  };

  const saveDashboardUpdates = async () => {
    const newDashboard = isTemplate
      ? await cloneDashboard(rootRef.current, title as string, description as string)
      : null;
    if (isTemplate && newDashboard) {
      addTemplate(newDashboard);
      const path = `/${router.query.appName as string}/${encodeURIComponent(newDashboard.name ?? '')}`;
      router.push(path).catch(() => {});
    } else {
      set(rootRef.current, 'title', title);
      set(rootRef.current, 'description', description);
      set(rootRef.current, 'templates', get(editingTemplate, 'templates', []));
      set(rootRef.current, 'spec', get(editingTemplate, 'spec', {}));
      set(rootRef.current, 'spec.editor.showDescriptionInViewMode', showDescription);

      view.syncLayout(rootRef.current);
      updateTemplate(rootRef.current);
      await saveTemplateChangesToOrion(rootRef.current);
      setDashboardViewerKey(Date.now());
    }
    switchToViewMode();
  };

  const saveWidgetUpdates = async () => {
    if (isWidgetEditMode) {
      const templates = get(editingTemplate, 'templates', []);

      for (let i = 0; i < templates.length; i++) {
        if (templates[i].name === editTemplateName) {
          if (getComponentTypeFromName(editTemplateName ?? '') !== ComponentType.FILTER) {
            // strip filterbar container for non-filter component types
            unset(viewingTemplate, 'templates');
            unset(viewingTemplate, 'spec.editor');
            get(viewingTemplate, 'spec.view', [] as any).shift();
          }

          // restore original template name for template being edited
          set(viewingTemplate, 'name', editTemplateName);
          // replace original template with updated template
          set(editingTemplate, `templates[${i}]`, viewingTemplate);
          break;
        }
      }

      setFilterBarAndUpdateTemplate(editingTemplate);
      enterDashboardEditMode();
    } else if (isEditMode) {
      await saveDashboardUpdates();
    }
  };

  const setTemplateSpecs = (tmpl: DashboardJson) => {
    const cp = getTemplateWithUpdatedExpressionFormat(cloneDeep(tmpl) as DashboardJson);

    setViewingTemplate(cp);
    setName(cp.name);
    setTitle(cp.title);
    setDescription(cp.description);
    setShowDescription(get(cp, 'spec.editor.showDescriptionInViewMode', true) as boolean);

    setPropsJSON(getWidgetPropertiesFromTemplate(cp));
    setDatasourceJSON(get(cp, 'spec.datasources', {}) as Record<string, Datasource>);
    setExpressionJSON(get(cp, 'spec.expressions', {}) as Record<string, ExpressionJson>);
  };

  const onTemplateFetched = (res: { data: DashboardJson }) => {
    setIsStale(true);
    if (res && res.data) {
      setFilterBarAndUpdateTemplate(res.data);
    } else {
      router.push(`/${router.query.appName}`);
    }
  };

  /**
   * Sets the root template's spec.editor.filterBar
   * which is utilized by JSONATA expressions in spec.view
   * as props for editable:FilterBar
   */
  const setFilterBarAndUpdateTemplate = (tmpl: DashboardJson) => {
    setFilterBar(tmpl);
    setTemplateSpecs(tmpl);
    setDashboardViewerKey(Date.now());
  };

  const setFilterBar = (tmpl: DashboardJson, editModeOverride?: boolean) => {
    const templates = get(tmpl, 'templates', []) as DashboardJson[];
    const templateType = getComponentTypeFromName(tmpl.name ?? '');

    const filters = reduce(
      templates,
      (mem, tmpl) => {
        if (tmpl.name?.includes(':filter:')) {
          mem.push({
            name: tmpl.name,
            varName: get(
              tmpl,
              'spec.widget.component.props.varName',
              get(tmpl, `${UNCONTAINED_WIDGET_JSON_POINTERS.PROPS}.varName`, ''),
            ),
            filterKey: get(
              tmpl,
              'spec.widget.component.props.filterKey',
              get(tmpl, `${UNCONTAINED_WIDGET_JSON_POINTERS.PROPS}.filterKey`, ''),
            ),
            label: get(
              tmpl,
              'spec.widget.component.props.title',
              get(tmpl, `${UNCONTAINED_WIDGET_JSON_POINTERS.PROPS}.title`, ''),
            ),
          });
        }
        return mem;
      },
      [] as any,
    );

    // add filter cfg
    set(tmpl, 'spec.editor.filterBar', filters);

    // update filter hash
    const keysByVar = reduce(
      filters,
      (mem, f) => {
        mem[f.varName] = f.filterKey;
        return mem;
      },
      {} as any,
    );

    setFilterKeysByVarName(keysByVar);

    // add filter bar
    if (templateType !== ComponentType.FILTER) {
      const viewSpec = get(tmpl, 'spec.view', []) as any[];

      if ((get(viewSpec, '[0][0]', '') as string) !== 'editable:FilterBar') {
        viewSpec.unshift([
          'editable:FilterBar',
          {
            className: editModeOverride === false ? 'pb-4' : '',
            editMode: editModeOverride ?? '/${ globals.editMode }',
            filterList: '/${ editor.filterBar }',
          },
          '/${ editor.filterBar.name.[$][] }',
        ]);

        set(tmpl, 'spec.view', viewSpec);
      }
    }

    updateTemplate(tmpl);
  };

  const dataPanelContainer = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [containerHeight, setContainerHeight] = useState(300);

  const startResizing = useCallback((mouseDownEvent: React.MouseEvent<HTMLDivElement>) => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (mouseMoveEvent: MouseEvent) => {
      if (isResizing) {
        setContainerHeight(window.innerHeight - mouseMoveEvent.clientY);
      }
    },
    [isResizing],
  );

  const onInspectQuery = (queryKey: string) => {
    setSelectedInspectedQueryKey(queryKey);
    setIsInspectMode(true);
  };

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    e.preventDefault();
    router.push(`/${router.query.appName}`).catch(() => {});
  };

  return (
    <Overlay.Provider data-testid="dashify-editor">
      <div className={`flex flex-col h-[calc(100vh-56px)]${isWidgetEditMode ? ' border-2 overflow-hidden' : ''}`}>
        {showPageTitle && (
          <div>
            {/* Dashboard view mode back to List page */}
            {!isEditMode && !isWidgetEditMode && (
              <div className="px-8 pt-4">
                <a onClick={handleClick} className={LINK_STYLES}>
                  <Icon.Navigation.ArrowLeft size="SMALL" style={{ margin: '0 2px 0 -3px' }} />
                  Dashboards
                </a>
              </div>
            )}
            {/* Editor Controls */}
            {(isEditMode || isWidgetEditMode) && (
              <DashifyEditorHeaderControls
                data-testid="dashify-editor-header-controls"
                template={rootRef.current}
                isEditMode={isEditMode}
                isWidgetEditMode={isWidgetEditMode}
                canUndo={undoRedoActions.canUndoAction}
                canRedo={undoRedoActions.canRedoAction}
                onAddWidget={widgetActions.onAddWidget}
                onCancel={revertEditsAndSwitchToViewMode}
                onSave={saveWidgetUpdates}
                undoRedoEvent={undoRedoHandler}
              />
            )}
          </div>
        )}

        {/* Dashboard Edit Header */}
        {!isWidgetEditMode && showPageTitle && (
          <div className="px-8">
            {/* Title & Time Range Button Group */}
            <DashifyHeader
              templateName={name ?? ''}
              isEditMode={isEditMode}
              isWidgetEditMode={isWidgetEditMode}
              title={title}
              setTitle={setTitle}
              enterEditMode={enterDashboardEditMode}
            />

            {/* Description */}
            <div className="py-1 flex items-center gap-2">
              {isEditMode && !isWidgetEditMode ? (
                <>
                  <DashifyDescription
                    description={description}
                    showDescription={showDescription}
                    updateDescription={setDescription}
                    updateDescriptionState={setShowDescription}
                  />
                </>
              ) : (
                <>{showDescription && description}</>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-grow">
          <div className="flex flex-col grow">
            <div data-testid="fso-dashify" id="fso-dashify" className={`grow my-2 px-4 grid overflow-auto`}>
              {/* Template Content */}
              <Layout.Provider
                defaults={{ h: 4, minW: 4, minH: 2 }}
                widget={widgetActions}
                editing={isEditMode && !isWidgetEditMode}
              >
                <DashboardViewer
                  data-testid="dashboard-viewer"
                  key={viewerKey}
                  dashboardName={name as string}
                  filter={filter}
                  isEditorMode={true}
                  useDashifyEditor={true}
                  showPageTitle={false}
                  fetchFromOrion={!isStale}
                  onTemplateFetched={onTemplateFetched}
                />
              </Layout.Provider>
            </div>
            {isWidgetEditMode && (
              <div
                className="resizer cursor-row-resize resize-y flex basis-[6px] hover:h-[3px] hover:bg-neutral-200"
                onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => {
                  startResizing(event);
                  event.stopPropagation();
                }}
              />
            )}

            {/* Query Builder */}
            {isWidgetEditMode && (
              <div
                className="overflow-auto"
                ref={dataPanelContainer}
                style={{ height: containerHeight, borderTop: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}
              >
                <DataPanel
                  data-testid="dashify-editor-data-panel"
                  filters={get(globals, 'variables.filters', {})}
                  datasources={datasourceJSON}
                  expressionJson={expressionsJSON}
                  onInspect={queryIndex => onInspectQuery(queryIndex)}
                  onSave={setQuery}
                />
              </div>
            )}
          </div>

          {/* Right Panel */}
          {/* Widget Edit Mode */}
          {isWidgetEditMode && (
            <DashifyPropertiesPanel
              selectedComponentType={selectedComponentType}
              propsJSON={propsJSON}
              setProps={setProps}
              isInspectMode={isInspectMode}
              expressions={expressionsJSON}
              queryResult={queryResult}
            />
          )}

          {/* Inspect Data Mode */}
          {isWidgetEditMode && isInspectMode && (
            <DashifyInspectPanel
              fullInspectMode={isFullInspectMode}
              queryKey={selectedInspectedQueryKey}
              queryResult={queryResult}
              expressionsJSON={expressionsJSON}
              stageExpressionUpdate={stageExpressionUpdate}
              setFullInspectMode={setFullInspectMode}
              setInspectMode={setIsInspectMode}
            />
          )}
        </div>
      </div>

      <Drawer.Portal />
    </Overlay.Provider>
  );
};
